import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { waLink } from '../../lib/wa.js';
import { SearchableSelect } from '../primitives/SearchableSelect.js';
import { WaIcon } from '../primitives/WaIcon.js';
import { DEPLOY_DURATIONS } from '@cls/shared';
import { useReferenceList } from '../../features/reference/useReferenceList.js';

interface Project { id: string; code: string; name: string; status: string; }
interface Contact { id: string; name: string; role: string | null; phone: string | null; customer: string | null; }

interface ProjectMeta {
  id: string;
  code: string;
  name: string;
  address: string | null;
  postcode: string | null;
  supervisorId: string | null;
  clientPmId: string | null;
  clsManagerId: string | null;
}

interface Props {
  onClose: () => void;
  deploymentId: string;
  workerId: string;
  workerName: string;
  workerPhone?: string;
  currentProjectName: string;
  preselectTargetId?: string;
}

export function MoveWorkerModal({ onClose, deploymentId, workerId, workerName, workerPhone, currentProjectName, preselectTargetId }: Props) {
  const qc = useQueryClient();
  const [target, setTarget] = useState(preselectTargetId ?? '');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('07:30');
  const [duration, setDuration] = useState('1 week');
  const { values: durations } = useReferenceList('durations', DEPLOY_DURATIONS);
  const durationOptions = durations.includes(duration) ? durations : [duration, ...durations];
  const [supervisorId, setSupervisorId] = useState('');
  const [supTouched, setSupTouched] = useState(false);
  const [meet, setMeet] = useState('');
  const [requesterId, setRequesterId] = useState('');
  const [remove, setRemove] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: projects = [] } = useQuery({ queryKey: ['projects'], queryFn: () => api.get<Project[]>('/projects').then((r) => r.data) });
  const { data: contacts = [] } = useQuery({ queryKey: ['contacts'], queryFn: () => api.get<Contact[]>('/contacts').then((r) => r.data) });
  const { data: targetMeta } = useQuery({
    queryKey: ['project', target],
    queryFn: () => api.get<ProjectMeta>(`/projects/${target}`).then((r) => r.data),
    enabled: !!target,
  });
  const liveProjects = projects.filter((p) => p.status === 'live');
  const targetProject = liveProjects.find((p) => p.id === target);
  const supervisor = contacts.find((c) => c.id === supervisorId);

  useEffect(() => {
    if (supTouched || !targetMeta) return;
    const def = targetMeta.supervisorId || targetMeta.clientPmId || targetMeta.clsManagerId || '';
    setSupervisorId(def);
  }, [targetMeta, supTouched]);

  const preview = useMemo(() => {
    const first = workerName.split(' ')[0] ?? workerName;
    const code = targetMeta?.code ?? targetProject?.code ?? '';
    const name = targetMeta?.name ?? targetProject?.name ?? '(select project)';
    const address = targetMeta?.address ?? '—';
    const postcode = targetMeta?.postcode ?? '—';
    const supLine = supervisor ? `${supervisor.name}${supervisor.phone ? ` · ${supervisor.phone}` : ''}` : '(not set)';
    const meetLine = meet.trim() || 'Report to site office';
    const startLine = `${startDate || 'TBC'} at ${startTime}`;
    const lines = [
      `Hi ${first},`,
      ``,
      `This is CLS — you're being moved from ${currentProjectName} to:`,
      ``,
      `Project: ${code} — ${name}`,
      `Address: ${address}`,
      `Postcode: ${postcode}`,
      ``,
      `Start: ${startLine}`,
      `Duration: ${duration}`,
      ``,
      `Supervisor: ${supLine}`,
      `Meet: ${meetLine}`,
    ];
    if (notes.trim()) lines.push(``, `Notes: ${notes.trim()}`);
    lines.push(``, `Please confirm receipt of this message.`, ``, `— CLS Construction Logistics Services`);
    return lines.join('\n');
  }, [workerName, currentProjectName, targetMeta, targetProject, startDate, startTime, duration, supervisor, meet, notes]);

  const move = useMutation({
    mutationFn: async (sendWa: boolean) => {
      const res = await api.post(`/deployments/${deploymentId}/move`, {
        toProjectId: target,
        startDate,
        startTime,
        duration,
        removeFromCurrent: remove,
        supervisorId: supervisorId || null,
        requesterId: requesterId || null,
        meet: meet.trim() || null,
        notes: notes.trim() || null,
        waMessageSent: sendWa,
      });
      if (sendWa && workerPhone) {
        window.open(waLink(workerPhone, preview), '_blank');
      }
      return res;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deployments'] });
      qc.invalidateQueries({ queryKey: ['workers'] });
      qc.invalidateQueries({ queryKey: ['proximity', workerId] });
      toast.success('Worker moved', `${workerName} → ${targetProject?.name ?? target}`);
      onClose();
    },
    onError: () => toast.error('Move failed'),
  });

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">↔ Move / Deploy Worker to Another Project</div>
        <div className="modal-sub" id="mw-sub">{workerName} — currently on {currentProjectName}</div>
        <div className="form-row">
          <label className="form-label">Select Target Project</label>
          <SearchableSelect
            id="mw-project"
            value={target}
            onChange={setTarget}
            placeholder="— Select project —"
            options={liveProjects.map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
          />
        </div>
        <div className="fg2">
          <div className="form-row"><label className="form-label">Start Date</label><input type="date" className="form-input" id="mw-date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Start Time</label><input type="time" className="form-input" id="mw-time" value={startTime} onChange={(e) => setStartTime(e.target.value)} /></div>
        </div>
        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Duration</label>
            <select className="form-select" id="mw-dur" value={duration} onChange={(e) => setDuration(e.target.value)}>
              {durationOptions.map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div className="form-row">
            <label className="form-label">Supervisor / Contact</label>
            <SearchableSelect
              id="mw-sup"
              value={supervisorId}
              onChange={(v) => { setSupervisorId(v); setSupTouched(true); }}
              placeholder="— Select supervisor —"
              options={contacts.map((c) => ({ value: c.id, label: `${c.name}${c.role ? ` — ${c.role}` : ''}${c.customer ? ` · ${c.customer}` : ''}` }))}
            />
          </div>
        </div>
        <div className="form-row"><label className="form-label">Who to Meet on Arrival</label><input type="text" className="form-input" id="mw-meet" value={meet} onChange={(e) => setMeet(e.target.value)} /></div>
        <div className="form-row">
          <label className="form-label">Requested By <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>— client contact who asked for this worker</span></label>
          <SearchableSelect
            id="mw-requester"
            value={requesterId}
            onChange={setRequesterId}
            placeholder="— No requester —"
            options={contacts.map((c) => ({ value: c.id, label: `${c.name}${c.customer ? ` · ${c.customer}` : ''}${c.role ? ` — ${c.role}` : ''}` }))}
          />
        </div>
        <div className="form-row">
          <label className="form-label">Remove from current project?</label>
          <div style={{ display: 'flex', gap: 14, marginTop: 5 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12 }}><input type="radio" name="mw-remove" checked={remove} onChange={() => setRemove(true)} style={{ accentColor: 'var(--accent)' }} /> Yes — move</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12 }}><input type="radio" name="mw-remove" checked={!remove} onChange={() => setRemove(false)} style={{ accentColor: 'var(--accent)' }} /> No — keep on both</label>
          </div>
        </div>
        <div className="form-row"><label className="form-label">Notes</label><textarea className="form-textarea" id="mw-notes" style={{ minHeight: 60 }} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        <div className="form-row">
          <label className="form-label">WhatsApp Message Preview</label>
          <div className="wa-preview" style={{ background: 'rgba(37,211,102,.08)', border: '1px solid rgba(37,211,102,.3)', borderRadius: 8, padding: 10 }}>
            <div className="wa-lbl" style={{ fontSize: 9, color: '#25D366', fontWeight: 700, marginBottom: 5, display: 'inline-flex', alignItems: 'center', gap: 4 }}><WaIcon size={11} /> Message preview</div>
            <div id="mw-wa-preview" style={{ whiteSpace: 'pre-wrap', fontSize: 11 }}>{preview}</div>
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-wa" style={{ background: '#25D366', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 5 }} onClick={() => move.mutate(true)} disabled={move.isPending || !target || !startDate}><WaIcon size={13} /> Move + WhatsApp</button>
          <button className="btn btn-primary" onClick={() => move.mutate(false)} disabled={move.isPending || !target || !startDate}>
            {move.isPending ? 'Moving…' : '↔ Move Worker'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
